import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group/radio-group"
import { Textarea } from "@/components/ui/textarea/textarea"

interface TenantApplicationRejectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: RejectionFormData) => void
  loading?: boolean
}

export interface RejectionFormData {
  rejectionReason: string
  needsFollowup: boolean
  followupRequest: string
}

export function TenantApplicationRejectionDialog({
  open,
  onOpenChange,
  onSubmit,
  loading = false,
}: TenantApplicationRejectionDialogProps) {
  const [formData, setFormData] = useState<RejectionFormData>({
    rejectionReason: "",
    needsFollowup: false,
    followupRequest: "",
  })

  const handleSubmit = () => {
    onSubmit(formData)
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // ダイアログを閉じるときにフォームをリセット
      setFormData({
        rejectionReason: "",
        needsFollowup: false,
        followupRequest: "",
      })
    }
    onOpenChange(newOpen)
  }

  const isFormValid =
    formData.rejectionReason.trim() && (!formData.needsFollowup || formData.followupRequest.trim())

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[400px] p-6 rounded-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center">
          <DialogTitle className="text-base font-semibold text-slate-900">
            入居申請を棄却します
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* 棄却理由 */}
          <div className="space-y-2">
            <Label htmlFor="rejectionReason" className="text-sm font-semibold text-slate-600">
              棄却理由 <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="rejectionReason"
              value={formData.rejectionReason}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setFormData({ ...formData, rejectionReason: e.target.value })
              }
              className="min-h-[100px] text-sm resize-none"
              placeholder="棄却理由を入力してください"
            />
          </div>

          {/* フォローアップ必要性 */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-600">
              契約者フォローアップ <span className="text-red-500">*</span>
            </Label>
            <RadioGroup
              value={formData.needsFollowup ? "required" : "not_required"}
              onValueChange={(value) =>
                setFormData({
                  ...formData,
                  needsFollowup: value === "required",
                  followupRequest: value === "not_required" ? "" : formData.followupRequest,
                })
              }
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="required" id="followup-required" />
                <Label htmlFor="followup-required" className="text-sm font-normal cursor-pointer">
                  必要
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="not_required" id="followup-not-required" />
                <Label
                  htmlFor="followup-not-required"
                  className="text-sm font-normal cursor-pointer"
                >
                  不要
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* フォローアップ依頼内容 */}
          {formData.needsFollowup && (
            <div className="space-y-2">
              <Label htmlFor="followupRequest" className="text-sm font-semibold text-slate-600">
                ご依頼内容 <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="followupRequest"
                value={formData.followupRequest}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFormData({ ...formData, followupRequest: e.target.value })
                }
                className="min-h-[80px] text-sm resize-none"
                placeholder="フォローアップの依頼内容を入力してください"
              />
            </div>
          )}

          {/* 注意書き */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-xs text-amber-800">
              棄却すると、チャットに自動返信メッセージが送信され、ステータスが「OC対応中」に変更されます。
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 pt-3">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            className="w-full"
            disabled={loading}
          >
            キャンセル
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={!isFormValid || loading}
            loading={loading}
            className="w-full"
          >
            棄却する
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
